import { Autorenew, Check, Close, PriorityHigh, Remove } from '@mui/icons-material';
import {
    BonusSettlementStatus,
    BonusSettlementSupplierStatus,
    DemandState,
    DemandTopicStatus,
    DocumentStatus,
    EmployeeAccess,
    ImportStatus,
    ImportSupplierStatus,
    InquiryOfferState,
    OfferStatus,
    SalesOrderStatus,
    SponsorBannerStatus,
    StatusStateType,
    UserStatus,
} from '@synergy/models';
import { Chip } from '@mui/material';
import { PropsWithChildren, ReactElement, ReactNode, createContext, useContext } from 'react';

/**
 * PALO five-step status scale (design study #2132). Every document state maps
 * onto one of five steps; each step renders as a soft pill (see theme-palo's
 * MuiChip override). The accent colour is never used as a solid status fill.
 */
export enum PaloStatusStep {
    Draft = 'default', // Entwurf
    InProgress = 'info', // In Arbeit
    WaitingForMe = 'warning', // Wartet auf mich
    Problem = 'error', // Problem / überfällig
    Done = 'success', // Abgeschlossen
}

const S = PaloStatusStep;

const stepByStatus: { [Status in StatusStateType]: PaloStatusStep } = {
    [DocumentStatus.Created]: S.Draft,
    [DocumentStatus.Ongoing]: S.InProgress,
    [DocumentStatus.Completed]: S.Done,
    // "Finished without a follow-up document" is a task now, not the normal
    // end: an order reaches it only at a charging supplier, 35 days after it
    // was completed without an invoice, and an inquiry 14 days after it ended
    // without an order (GF 15.10.2026, #2264). The orders of suppliers who bill
    // the member directly stay "Beendet", so red marks what is to be done.
    [DocumentStatus.CompletedWithoutTransition]: S.Problem,
    [DocumentStatus.CompletedWithTransition]: S.Done,
    [DocumentStatus.Canceled]: S.Problem,
    [DocumentStatus.Scheduled]: S.InProgress,
    [DocumentStatus.Paused]: S.WaitingForMe,

    [DemandState.Open]: S.Draft,
    [DemandState.NoDemand]: S.WaitingForMe,
    [DemandState.DemandReported]: S.InProgress,
    [DemandState.ClosedNoDemand]: S.Problem,
    [DemandState.ClosedWithDemand]: S.Done,

    [OfferStatus.Pending]: S.WaitingForMe,
    [OfferStatus.Accepted]: S.Done,
    [OfferStatus.Declined]: S.Problem,

    [InquiryOfferState.Open]: S.Draft,
    [InquiryOfferState.OnGoing]: S.InProgress,
    [InquiryOfferState.Closed]: S.Done,
    [InquiryOfferState.Declined]: S.Problem,

    [UserStatus.ShopAdmin]: S.InProgress,
    [UserStatus.SupplierAdmin]: S.InProgress,
    [UserStatus.NoUser]: S.Draft,
    [UserStatus.Locked]: S.Problem,
    [UserStatus.ShopUser]: S.Done,
    [UserStatus.SupplierUser]: S.Done,
    [UserStatus.NotActivated]: S.Problem,

    // No login is the normal state for most employees, not a fault; a pending
    // invitation is waiting on the other side; blocked and switched off are.
    [EmployeeAccess.None]: S.Draft,
    [EmployeeAccess.Invited]: S.WaitingForMe,
    [EmployeeAccess.Locked]: S.Problem,
    [EmployeeAccess.Disabled]: S.Problem,
    [EmployeeAccess.Active]: S.Done,

    [ImportStatus.Receiving]: S.InProgress,
    [ImportStatus.DeleteExistingProducts]: S.InProgress,
    [ImportStatus.CreatingCategories]: S.InProgress,
    [ImportStatus.ReadyToPublish]: S.WaitingForMe,
    [ImportStatus.Calculating]: S.InProgress,
    [ImportStatus.Published]: S.Done,
    [ImportStatus.Aborted]: S.Problem,
    [ImportStatus.Publishing]: S.InProgress,
    [ImportStatus.Archived]: S.Done,

    [DemandTopicStatus.Assigned]: S.Done,
    [DemandTopicStatus.Unassigned]: S.Draft,
    [DemandTopicStatus.NotInterested]: S.WaitingForMe,

    [SalesOrderStatus.CartPending]: S.Draft,
    [SalesOrderStatus.CompletedWithApproval]: S.Done,

    [BonusSettlementStatus.Draft]: S.Draft,
    [BonusSettlementStatus.Sent]: S.WaitingForMe,
    [BonusSettlementStatus.Completed]: S.Done,
    [BonusSettlementStatus.ReadyToComplete]: S.WaitingForMe,
    [BonusSettlementStatus.Pending]: S.WaitingForMe,

    [BonusSettlementSupplierStatus.AutomaticallySent]: S.WaitingForMe,
    [BonusSettlementSupplierStatus.PartiallySent]: S.WaitingForMe,
    [BonusSettlementSupplierStatus.Reclaimed]: S.Problem,

    [SponsorBannerStatus.Active]: S.Done,
    [SponsorBannerStatus.Inactive]: S.Draft,

    [ImportSupplierStatus.OnSchedule]: S.Done,
    [ImportSupplierStatus.Overdue]: S.Problem,
    [ImportSupplierStatus.NoInterval]: S.Draft,
};

/**
 * The configured mapping, supplied at boot from synergy_config.status_scale so
 * the customer can move a state onto another step without a deploy. Anything it
 * does not name keeps the built-in step, which is what a fresh installation and
 * a newly added state fall back to.
 */
const PaloStatusScaleContext = createContext<Record<string, string> | null>(null);

export const PaloStatusScaleProvider = ({
    scale,
    children,
}: PropsWithChildren<{ scale?: Record<string, string> | null }>): ReactElement => {
    // A provider inside another adds to the outer scale rather than replacing it,
    // so one screen can move a single state without losing the configured rest.
    const outer = useContext(PaloStatusScaleContext);
    const merged = outer || scale ? { ...(outer ?? {}), ...(scale ?? {}) } : null;

    return <PaloStatusScaleContext.Provider value={merged}>{children}</PaloStatusScaleContext.Provider>;
};

const isStep = (value: string): value is PaloStatusStep => (Object.values(PaloStatusStep) as string[]).includes(value);

/**
 * States the configured scale may not move.
 *
 * A cancelled receipt has to be distinguishable from a valid one wherever it
 * appears — in a list of thousands, in the head of the document, in a printed
 * page. Which step the other states sit on is a matter of how a house reads
 * its own work; this one is not, and a scale that put it on "done" made 1.511
 * cancelled orders look settled (#2216, R-106).
 */
const FIXED_STEPS: Record<string, PaloStatusStep> = {
    [DocumentStatus.Canceled]: PaloStatusStep.Problem,
};

/** Whether this state's step is the built-in one, whatever the scale says. */
export const paloStatusStepIsFixed = (status: StatusStateType): boolean => status in FIXED_STEPS;

export const paloStatusColor = (status: StatusStateType): PaloStatusStep => stepByStatus[status];

const useStatusStep = (status: StatusStateType): PaloStatusStep => {
    const configured = useContext(PaloStatusScaleContext)?.[status];
    const fixed = FIXED_STEPS[status];

    if (undefined !== fixed) {
        return fixed;
    }

    return configured && isStep(configured) ? configured : stepByStatus[status];
};

/**
 * Each step also carries a sign, so the scale stays readable where colour does
 * not arrive: colour blindness, a greyscale print of a list, a glance at a dense
 * table. The signs are the colour concept's: – draft, ⟳ running, ! mind it,
 * ✓ done, and on the red step × for cancelled, ! for everything else that
 * needs doing (GF 15.10.2026, #2290).
 */
const glyphByStep: Record<PaloStatusStep, typeof Check> = {
    [PaloStatusStep.Draft]: Remove,
    [PaloStatusStep.InProgress]: Autorenew,
    [PaloStatusStep.WaitingForMe]: PriorityHigh,
    [PaloStatusStep.Problem]: PriorityHigh,
    [PaloStatusStep.Done]: Check,
};

export const PaloStatusPill = ({ status, label }: { status: StatusStateType; label: ReactNode }): ReactElement => {
    const step = useStatusStep(status);
    const Glyph = DocumentStatus.Canceled === status ? Close : glyphByStep[step];

    return (
        <Chip
            size="small"
            color={step}
            icon={<Glyph />}
            label={label}
            sx={{
                // The theme puts a dot before every chip label; the glyph takes
                // its place here, so the two do not stack up.
                '& .MuiChip-label::before': { display: 'none' },
                '& .MuiChip-icon': {
                    fontSize: 11,
                    marginLeft: '7px',
                    marginRight: '-3px',
                    color: 'inherit',
                },
            }}
        />
    );
};
